<template>
  <Teleport to="body">
    <Transition name="modal-fade">
      <div v-if="visible" class="modal-overlay" @click.self="close">
      <div class="custom-modal">
        <div class="modal-header">
          <h3>官方更新日志</h3>
          <button class="close-btn" @click="close">
            <i class="fa-solid fa-times"></i>
          </button>
        </div>
        
        <div class="modal-body">
          <!-- AI 翻译控制栏 -->
          <div v-if="rawMarkdown" class="translation-bar" style="margin-bottom: 20px; display: flex; justify-content: space-between; align-items: center; background: rgba(0, 0, 0, 0.03); padding: 8px 15px; border-radius: 12px; border: 1px solid rgba(0, 0, 0, 0.05); font-size: 0.9em;">
            <div class="trans-info" style="color: #555; font-family: '猫啃网糖圆体';">
              <template v-if="isLoggedIn">
                <span>积分：<strong style="color: #0071e3; font-weight: bold;">{{ formatPoints(userPoints) }}</strong> 点</span>
                <button 
                  v-if="!hasCheckedInToday" 
                  @click="handleCheckIn" 
                  :disabled="isCheckingIn"
                  style="margin-left: 8px; padding: 2px 8px; font-size: 0.85em; border-radius: 8px; background: linear-gradient(135deg, #4CAF50 0%, #0071e3 100%); color: white; border: none; cursor: pointer;"
                >
                  {{ isCheckingIn ? '签到中...' : '签到' }}
                </button>
              </template>
              <template v-else>
                <span>登录后可使用免费 AI 翻译</span>
              </template>
            </div>
            <div class="trans-actions">
              <template v-if="isLoggedIn">
                <!-- 后台翻译进行中：显示进度，允许关闭弹窗稍后再看 -->
                <template v-if="isTranslateRunning">
                  <span class="trans-running">
                    <i class="fa-solid fa-circle-notch fa-spin"></i>
                    后台翻译中 {{ translateProgress }}%
                  </span>
                  <span class="trans-running-tip">可关闭弹窗，完成后会收到站内通知</span>
                </template>
                <template v-else-if="!isTranslated">
                  <span v-if="translateStatus !== 'failed'" style="font-size: 0.82em; color: #888; margin-right: 4px;">约{{ estimatedTranslateCost.toFixed(2) }}积分</span>
                  <span v-else style="font-size: 0.82em; color: #ff6b6b; margin-right: 4px;">{{ translateError || '上次翻译失败，积分已退还' }}</span>
                  <button
                    @click="translateLog"
                    :disabled="!rawMarkdown"
                    style="padding: 4px 12px; border-radius: 10px; background: linear-gradient(135deg, #4CAF50 0%, #0071e3 100%); color: white; border: none; cursor: pointer; font-family: '爱点风雅黑'; display: inline-flex; align-items: center; justify-content: center; gap: 4px;"
                  >
                    <i class="fa-solid fa-language" style="display: inline-flex; align-items: center; justify-content: center; width: 1.1em; height: 1.1em; line-height: 1;"></i>
                    {{ translateStatus === 'failed' ? '重新翻译' : 'AI 后台翻译' }}
                  </button>
                </template>
                <button
                  v-else
                  @click="toggleTranslation"
                  style="padding: 4px 12px; border-radius: 10px; background: #888; color: white; border: none; cursor: pointer; font-family: '爱点风雅黑'; display: inline-flex; align-items: center; justify-content: center; gap: 4px;"
                >
                  <i class="fa-solid fa-repeat" style="display: inline-flex; align-items: center; justify-content: center; width: 1.1em; height: 1.1em; line-height: 1;"></i>
                  {{ showTranslated ? '显示原文' : '显示翻译' }}
                </button>
              </template>
              <template v-else>
                <span style="color: #ff6b6b; font-weight: bold; font-size: 0.9em;">请先登录账户</span>
              </template>
            </div>
          </div>

          <div v-if="releaseNoteHtml" class="markdown-body release-note-content" v-html="releaseNoteHtml"></div>
          <div v-else class="empty-tip">暂无更新日志内容</div>
        </div>
        
        <div class="modal-footer">
          <button class="confirm-btn" @click="close">我知道了</button>
        </div>
        <SlideCaptcha v-model:visible="captchaVisible" @success="onCaptchaSuccess" />
      </div>
    </div>
  </Transition>
  </Teleport>
</template>

<script>
import eventBus from '@/utils/eventBus'
import { marked } from 'marked'
import DOMPurify from 'dompurify'
import axios from 'axios'
import serverUrl from '@/assets/BaseServerUrl.js'
import { message } from '@/utils/ui-service'
import translateService from '@/services/translateService'
import SlideCaptcha from '@/components/SlideCaptcha.vue'

const TRANSLATE_TYPE = 'release_note'

export default {
  name: 'ReleaseNoteModal',
  components: {
    SlideCaptcha
  },
  data() {
    return {
      captchaVisible: false,
      verifiedCaptchaToken: '',
      visible: false,
      rawMarkdown: '',
      releaseNoteHtml: '',
      translatedMarkdown: '',
      isTranslated: false,
      showTranslated: false,
      translateStatus: 'idle',
      translateProgress: 0,
      translateError: '',
      targetId: '',
      targetTitle: '',
      unsubscribeTranslate: null,
      userPoints: 0,
      hasCheckedInToday: false,
      isCheckingIn: false,
      isLoggedIn: false
    }
  },
  computed: {
    estimatedTranslateCost() {
      const charCount = (this.rawMarkdown || '').trim().length
      return Math.round(Math.min(5.00, Math.max(0.10, charCount / 500 * 0.10)) * 100) / 100
    },
    isTranslateRunning() {
      return this.translateStatus === 'queued' || this.translateStatus === 'processing'
    }
  },
  watch: {
    visible(open) {
      if (!open) this.detachTranslateListener()
    }
  },
  mounted() {
    eventBus.on('show-release-note', (payload) => {
      const content = typeof payload === 'string' ? payload : (payload.markdown || '')
      this.targetId = (payload && payload.appId) ? String(payload.appId) : ''
      this.targetTitle = (payload && payload.title) || ''

      this.rawMarkdown = content.trim().startsWith('<') ? '' : content
      this.showTranslated = false
      this.releaseNoteHtml = content.trim().startsWith('<')
        ? content
        : this.parseMarkdown(content)

      this.isLoggedIn = localStorage.getItem('isLoggedIn') === 'true'
      if (this.isLoggedIn) {
        this.fetchPointsInfo()
        this.restoreTranslateState()
      } else {
        this.isTranslated = false
        this.translatedMarkdown = ''
        this.translateStatus = 'idle'
      }

      this.visible = true
    })
  },
  beforeUnmount() {
    this.detachTranslateListener()
    eventBus.off('show-release-note')
  },
  methods: {
    formatPoints(val) {
      if (val == null) return '0.00'
      return Number(val).toFixed(2)
    },
    close() {
      this.visible = false
    },
    parseMarkdown(text) {
      if (!text) return ''
      try {
        const rawHtml = marked(text)
        return DOMPurify.sanitize(rawHtml)
      } catch (e) {
        console.error('Markdown parsing failed:', e)
        return text
      }
    },
    translateTarget() {
      return { type: TRANSLATE_TYPE, targetId: this.targetId }
    },
    // 重开弹窗时若后台已有结果或仍在跑，直接恢复，不重复发起也不丢进度
    restoreTranslateState() {
      const snapshot = translateService.peek(this.translateTarget())
      if (!snapshot) {
        this.isTranslated = false
        this.translatedMarkdown = ''
        this.translateStatus = 'idle'
        this.translateProgress = 0
        return
      }
      this.applyTranslateSnapshot(snapshot)
      if (snapshot.status === 'queued' || snapshot.status === 'processing') {
        this.attachTranslateListener()
      }
    },
    attachTranslateListener() {
      if (this.unsubscribeTranslate) return
      this.unsubscribeTranslate = translateService.subscribe(this.translateTarget(), (snapshot) => {
        this.applyTranslateSnapshot(snapshot)
      })
    },
    detachTranslateListener() {
      if (this.unsubscribeTranslate) {
        this.unsubscribeTranslate()
        this.unsubscribeTranslate = null
      }
    },
    applyTranslateSnapshot(snapshot) {
      const wasRunning = this.isTranslateRunning
      this.translateStatus = snapshot.status
      this.translateProgress = snapshot.progress || 0
      if (snapshot.status === 'completed' && snapshot.translatedText) {
        this.translatedMarkdown = snapshot.translatedText
        this.isTranslated = true
        this.showTranslated = true
        this.releaseNoteHtml = this.parseMarkdown(snapshot.translatedText)
        this.detachTranslateListener()
        this.fetchPointsInfo()
        if (wasRunning) {
          message({
            message: snapshot.cached
              ? '已命中历史译文'
              : `翻译完成！消耗 ${Number(snapshot.costPoints || 0).toFixed(2)} 积分`,
            type: 'success'
          })
        }
      } else if (snapshot.status === 'failed') {
        this.translateError = snapshot.error || '翻译失败，积分已退还'
        this.isTranslated = false
        this.detachTranslateListener()
        if (wasRunning) message({ message: this.translateError, type: 'error' })
      }
    },
    // 获取当前用户积分及签到信息
    async fetchPointsInfo() {
      if (!this.isLoggedIn) return
      try {
        const response = await axios.get(`${serverUrl}/api/user/points`)
        if (response.data.success) {
          this.userPoints = response.data.points || 0
          this.hasCheckedInToday = response.data.checkedIn || false
        }
      } catch (error) {
        console.warn('获取用户积分失败:', error.message)
      }
    },
    // 处理签到
    async handleCheckIn() {
      if (!this.isLoggedIn || this.hasCheckedInToday) return

      if (!this.verifiedCaptchaToken) {
        this.captchaVisible = true
        return
      }

      this.isCheckingIn = true
      try {
        const response = await axios.post(`${serverUrl}/api/user/checkin`, {
          captcha: this.verifiedCaptchaToken
        })
        if (response.data.success) {
          this.userPoints = response.data.points
          this.hasCheckedInToday = true
          message({
            message: response.data.message || '签到成功！',
            type: 'success'
          })
          // 发送全局事件，使得 AppDetails 也能同步积分
          eventBus.emit('points-updated', this.userPoints)
        }
      } catch (error) {
        console.error('签到失败:', error)
        const msg = error.response?.data?.message || '签到失败，请稍后重试'
        message({
          message: msg,
          type: 'error'
        })
      } finally {
        this.isCheckingIn = false
        this.verifiedCaptchaToken = ''
      }
    },
    onCaptchaSuccess(token) {
      this.verifiedCaptchaToken = token
      this.handleCheckIn()
    },
    // 提交后台翻译任务：立即返回，完成后由 socket/轮询回填
    async translateLog() {
      if (!this.isLoggedIn) return
      if (!this.rawMarkdown) return

      if (this.isTranslated) {
        this.showTranslated = true
        this.releaseNoteHtml = this.parseMarkdown(this.translatedMarkdown)
        return
      }

      this.translateStatus = 'queued'
      this.translateProgress = 0
      this.translateError = ''
      this.attachTranslateListener()

      try {
        await translateService.requestTranslate({
          text: this.rawMarkdown,
          type: TRANSLATE_TYPE,
          targetId: this.targetId,
          targetTitle: this.targetTitle
        })
      } catch (error) {
        // applyTranslateSnapshot 已经提示过，这里只兜底未捕获的提交异常
        const msg = error?.message || '翻译失败，请稍后重试'
        if (this.translateStatus !== 'failed') {
          this.translateStatus = 'failed'
          this.translateError = msg
          message({ message: msg, type: 'error' })
        }
      }
    },
    // 切换原文与译文展示
    toggleTranslation() {
      this.showTranslated = !this.showTranslated
      const textToShow = this.showTranslated ? this.translatedMarkdown : this.rawMarkdown
      this.releaseNoteHtml = this.parseMarkdown(textToShow)
    }
  }
}
</script>

<style scoped>
* {
  box-sizing: border-box;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: var(--z-dialog);
  backdrop-filter: blur(4px);
}

.custom-modal {
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 20px;
  width: 90%;
  max-width: 600px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  max-height: 85vh;
}

.modal-header {
  padding: 20px 25px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-header h3 {
  margin: 0;
  font-family: '猫啃网糖圆体';
  font-size: 1.3em;
  color: #333;
}

.close-btn {
  background: rgba(0, 0, 0, 0.05);
  border: none;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  font-size: 16px;
  cursor: pointer;
  color: #666;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s;
}

.close-btn:hover {
  background: rgba(255, 68, 68, 0.1);
  color: #ff4444;
}

.modal-body {
  padding: 25px;
  flex: 1;
  overflow-y: auto;
  line-height: 1.7;
}

.markdown-body {
  font-family: '爱点风雅黑', sans-serif;
  color: #444;
}

:deep(.markdown-body h1), 
:deep(.markdown-body h2), 
:deep(.markdown-body h3),
:deep(.markdown-body h4),
:deep(.markdown-body h5),
:deep(.markdown-body h6) {
  margin-top: 1.2em;
  margin-bottom: 0.8em;
  font-family: '猫啃网糖圆体';
  color: #222;
  line-height: 1.4;
}

:deep(.markdown-body p) {
  margin-bottom: 1em;
}

:deep(.markdown-body ul), :deep(.markdown-body ol) {
  padding-left: 20px;
  margin-bottom: 1em;
}

:deep(.markdown-body code) {
  background: rgba(0, 0, 0, 0.05);
  padding: 2px 5px;
  border-radius: 4px;
  font-family: monospace;
}

:deep(.markdown-body blockquote) {
  margin: 1em 0;
  padding: 0.5em 1em;
  color: #666;
  border-left: 4px solid #ddd;
  background: rgba(0, 0, 0, 0.02);
}

:deep(.markdown-body mark) {
  background-color: #d1ffd1;
  color: #111;
  padding: 2px 4px;
  border-radius: 4px;
}

:deep(.markdown-body hr) {
  height: 1px;
  border: none;
  background: rgba(0, 0, 0, 0.1);
  margin: 1.5em 0;
}

:deep(.markdown-body table) {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 1em;
}

:deep(.markdown-body th), :deep(.markdown-body td) {
  border: 1px solid #eee;
  padding: 8px 12px;
  text-align: left;
}

:deep(.markdown-body th) {
  background: #f8f9fa;
}

.empty-tip {
  color: #999;
  text-align: center;
  padding: 40px 0;
}

.trans-running {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border-radius: 10px;
  background: linear-gradient(135deg, #4CAF50 0%, #0071e3 100%);
  color: #fff;
  font-family: '爱点风雅黑';
  font-size: 0.92em;
}

.trans-running-tip {
  margin-left: 8px;
  font-size: 0.8em;
  color: #888;
}

.modal-footer {
  padding: 15px 25px;
  border-top: 1px solid rgba(0, 0, 0, 0.05);
  display: flex;
  justify-content: center;
}

.confirm-btn {
  padding: 10px 40px;
  border: none;
  background: linear-gradient(135deg, #4CAF50 0%, #0071e3 100%);
  color: white;
  border-radius: 25px;
  cursor: pointer;
  font-family: '爱点风雅黑';
  font-weight: 600;
  transition: all 0.3s;
  box-shadow: 0 4px 15px rgba(76, 175, 80, 0.2);
}

.confirm-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(76, 175, 80, 0.3);
}

.dark-theme .modal-footer {
  border-top-color: rgba(255, 255, 255, 0.05);
}

/* Animations */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-fade-enter-active .custom-modal {
  transition: transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.modal-fade-leave-active .custom-modal {
  transition: transform 0.3s ease-in;
}

.modal-fade-enter-from .custom-modal {
  transform: scale(0.9) translateY(20px);
}

.modal-fade-leave-to .custom-modal {
  transform: scale(0.95) translateY(20px);
}

@media (max-width: 768px) {
  .custom-modal {
    max-width: 90%;
  }
}

@media (max-width: 480px) {
  .custom-modal {
    width: 100%;
    border-radius: 25px 25px 0 0;
    position: absolute;
    bottom: 0;
    margin: 0;
    max-width: none;
    max-height: 85vh;
  }
  
  .modal-overlay {
    align-items: flex-end;
  }
  
  .modal-fade-enter-from .custom-modal,
  .modal-fade-leave-to .custom-modal {
    transform: translateY(100%);
  }
  
  .modal-header {
    padding: 15px 20px;
  }
  
  .modal-body {
    padding: 20px;
  }
}
</style>

<style>
/* Global Dark Theme Overrides for ReleaseNoteModal */
body.dark-theme .custom-modal {
  background: rgba(45, 45, 45, 0.9) !important;
  border-color: rgba(255, 255, 255, 0.1) !important;
}

body.dark-theme .modal-header h3 {
  color: #ffffff !important;
}

body.dark-theme .markdown-body.release-note-content {
  color: #d1d1d1 !important;
}

body.dark-theme .markdown-body.release-note-content h1,
body.dark-theme .markdown-body.release-note-content h2,
body.dark-theme .markdown-body.release-note-content h3,
body.dark-theme .markdown-body.release-note-content h4,
body.dark-theme .markdown-body.release-note-content h5,
body.dark-theme .markdown-body.release-note-content h6 {
  color: #ffffff !important;
}

body.dark-theme .markdown-body.release-note-content mark {
  background-color: #2d4a2d !important;
  color: #d1ffd1 !important;
}

body.dark-theme .markdown-body.release-note-content blockquote {
  color: #aaaaaa !important;
  border-left-color: #444444 !important;
  background: rgba(255, 255, 255, 0.03) !important;
}

body.dark-theme .markdown-body.release-note-content code {
  background: rgba(255, 255, 255, 0.1) !important;
  color: #e0e0e0 !important;
}

body.dark-theme .markdown-body.release-note-content a {
  color: #409eff !important;
}

body.dark-theme .markdown-body.release-note-content hr {
  background: rgba(255, 255, 255, 0.1) !important;
}

body.dark-theme .markdown-body.release-note-content th,
body.dark-theme .markdown-body.release-note-content td {
  border-color: rgba(255, 255, 255, 0.1) !important;
}

body.dark-theme .markdown-body.release-note-content th {
  background: rgba(255, 255, 255, 0.05) !important;
  color: #eeeeee !important;
}
</style>
